import React, { useState, useEffect, useCallback } from 'react';
import { useBrand } from '../../context/BrandContext';
import {
  Box,
  Typography,
  Button,
  Alert,
  AlertTitle,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Stack,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Autocomplete,
  Tooltip,
  Card,
  CardContent,
  InputAdornment
} from '@mui/material';
import {
  Link as LinkIcon,
  AddCircle as AddCircleIcon,
  Search as SearchIcon,
  Refresh as RefreshIcon,
  Warning as WarningIcon
} from '@mui/icons-material';
import {
  getUnmappedSkus,
  getInventorySkus,
  createSkuAliasMapping,
  createNewInventorySku,
  formatNumber
} from '../../services/inventoryService';

/**
 * UnmappedSkuMappings Component
 * Shows SKUs that appear in orders/transfers/receipts but are not mapped to inventory
 * Allows mapping to existing inventory SKUs or creating new ones
 */
function UnmappedSkuMappings({ onDataChange }) {
  const { selectedBrand } = useBrand();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [unmappedSkus, setUnmappedSkus] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');

  // Mapping dialog state
  const [mappingDialogOpen, setMappingDialogOpen] = useState(false);
  const [selectedUnmappedSku, setSelectedUnmappedSku] = useState(null);
  const [inventorySkuOptions, setInventorySkuOptions] = useState([]);
  const [selectedInventorySku, setSelectedInventorySku] = useState(null);
  const [mappingLoading, setMappingLoading] = useState(false);
  const [inventorySkuLoading, setInventorySkuLoading] = useState(false);

  // Create new SKU dialog state
  const [createSkuDialogOpen, setCreateSkuDialogOpen] = useState(false);
  const [newSkuForm, setNewSkuForm] = useState({
    sku: '',
    product_name: ''
  });
  const [createSkuLoading, setCreateSkuLoading] = useState(false);

  const loadData = useCallback(async () => {
    if (!selectedBrand?.id) {
      setError('Please select a brand first');
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const res = await getUnmappedSkus({ brand_id: selectedBrand.id });
      setUnmappedSkus(res?.data?.unmapped_skus || []);
    } catch (err) {
      console.error('Failed to load unmapped SKUs:', err);
      setError('Failed to load unmapped SKUs. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [selectedBrand?.id]);

  useEffect(() => {
    if (selectedBrand?.id) {
      loadData();
    }
  }, [selectedBrand?.id, loadData]);

  // Open mapping dialog for an unmapped SKU
  const openMappingDialog = async (unmappedSku) => {
    setSelectedUnmappedSku(unmappedSku);
    setSelectedInventorySku(null);
    setInventorySkuOptions([]);
    setError(null); setMappingDialogOpen(true);

    // Load inventory SKUs for dropdown
    setInventorySkuLoading(true);
    try {
      const params = selectedBrand?.id ? { brand_id: selectedBrand.id } : {};
      const res = await getInventorySkus(params);
      setInventorySkuOptions(res?.data || []);
    } catch (err) {
      console.error('Failed to load inventory SKUs:', err);
      setError('Failed to load inventory SKUs for mapping');
    } finally {
      setInventorySkuLoading(false);
    }
  };

  // Submit SKU alias mapping
  const handleSubmitMapping = async () => {
    if (!selectedUnmappedSku || !selectedInventorySku) {
      setError('Please select an inventory SKU to map to');
      return;
    }

    setMappingLoading(true);
    try {
      await createSkuAliasMapping(
        selectedUnmappedSku.sku,
        selectedInventorySku.primary_sku,
        `Mapped from SKU Mappings tab`
      );

      setSuccess(`SKU "${selectedUnmappedSku.sku}" has been mapped to "${selectedInventorySku.primary_sku}"`);
      setMappingDialogOpen(false);

      // Reload data to refresh the list
      loadData();
      // Notify parent to refresh products/bundles/parts tabs
      onDataChange?.();
    } catch (err) {
      console.error('Failed to create SKU mapping:', err);
      setError(err.response?.data?.error || 'Failed to create SKU mapping');
    } finally {
      setMappingLoading(false);
    }
  };

  // Open create new SKU dialog
  const openCreateSkuDialog = (unmappedSku) => {
    setSelectedUnmappedSku(unmappedSku);
    setNewSkuForm({
      sku: unmappedSku.sku,
      product_name: ''
    });
    setError(null); setCreateSkuDialogOpen(true);
  };

  // Submit create new inventory SKU
  const handleCreateNewSku = async () => {
    if (!newSkuForm.sku) {
      setError('SKU is required');
      return;
    }

    setCreateSkuLoading(true);
    try {
      await createNewInventorySku(
        newSkuForm.sku,
        newSkuForm.product_name || newSkuForm.sku,
        selectedBrand?.id
      );

      setSuccess(`New inventory SKU "${newSkuForm.sku}" has been created`);
      setCreateSkuDialogOpen(false);

      // Reload data to refresh the list
      loadData();
      // Notify parent to refresh products/bundles/parts tabs
      onDataChange?.();
    } catch (err) {
      console.error('Failed to create inventory SKU:', err);
      setError(err.response?.data?.error || 'Failed to create inventory SKU');
    } finally {
      setCreateSkuLoading(false);
    }
  };

  // Clear messages after timeout
  useEffect(() => {
    if (success) {
      const timer = setTimeout(() => setSuccess(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [success]);

  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => setError(null), 8000);
      return () => clearTimeout(timer);
    }
  }, [error]);

  // Filter unmapped SKUs by search term
  const filteredSkus = unmappedSkus.filter(sku =>
    !searchTerm ||
    sku.sku?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    sku.gtin?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    sku.asin?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="300px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {/* Messages */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      {/* Summary Card */}
      {unmappedSkus.length > 0 ? (
        <Alert severity="warning" sx={{ mb: 2 }}>
          <AlertTitle>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <WarningIcon />
              {unmappedSkus.length} Unmapped SKUs Found
            </Box>
          </AlertTitle>
          <Typography variant="body2">
            These SKUs appear in orders, warehouse transfers, purchase orders, or fulfillments but are not mapped to any inventory SKU.
            Map them to existing inventory SKUs or create new inventory entries.
          </Typography>
        </Alert>
      ) : (
        <Alert severity="success" sx={{ mb: 2 }}>
          <AlertTitle>All SKUs Mapped</AlertTitle>
          <Typography variant="body2">
            All SKUs from orders and transactions are properly mapped to inventory. No action needed.
          </Typography>
        </Alert>
      )}

      {/* Search and Refresh */}
      <Box sx={{ display: 'flex', gap: 2, mb: 2, alignItems: 'center' }}>
        <TextField
          placeholder="Search by SKU, GTIN, or ASIN..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon color="action" />
              </InputAdornment>
            )
          }}
          size="small"
          sx={{ width: 300 }}
        />
        <Button
          startIcon={<RefreshIcon />}
          onClick={loadData}
          variant="outlined"
          size="small"
        >
          Refresh
        </Button>
        <Typography variant="body2" color="text.secondary" sx={{ ml: 'auto' }}>
          Showing {filteredSkus.length} of {unmappedSkus.length} unmapped SKUs
        </Typography>
      </Box>

      {/* Unmapped SKUs Table */}
      {filteredSkus.length > 0 ? (
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>SKU</TableCell>
                <TableCell>GTIN / ASIN</TableCell>
                <TableCell>Sources</TableCell>
                <TableCell align="right">Units</TableCell>
                <TableCell align="right">Transactions</TableCell>
                <TableCell align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredSkus.map((sku) => (
                <TableRow key={sku.sku} hover>
                  <TableCell>
                    <Typography variant="body2" fontWeight="medium">
                      {sku.sku}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Stack direction="column" spacing={0.5}>
                      {sku.gtin && (
                        <Typography variant="caption" color="text.secondary">
                          GTIN: {sku.gtin}
                        </Typography>
                      )}
                      {sku.asin && (
                        <Typography variant="caption" color="text.secondary">
                          ASIN: {sku.asin}
                        </Typography>
                      )}
                      {!sku.gtin && !sku.asin && (
                        <Typography variant="caption" color="text.disabled">
                          -
                        </Typography>
                      )}
                    </Stack>
                  </TableCell>
                  <TableCell>
                    <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                      {(sku.sources || []).map((source) => (
                        <Chip
                          key={source}
                          label={source}
                          size="small"
                          variant="outlined"
                          sx={{ fontSize: '0.7rem' }}
                        />
                      ))}
                    </Stack>
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="body2" color="warning.main" fontWeight="medium">
                      {formatNumber(sku.total_quantity)}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="body2">
                      {formatNumber(sku.transaction_count)}
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Stack direction="row" spacing={1} justifyContent="center">
                      <Tooltip title="Map to existing inventory SKU">
                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={<LinkIcon />}
                          onClick={() => openMappingDialog(sku)}
                        >
                          Map
                        </Button>
                      </Tooltip>
                      <Tooltip title="Create as new inventory SKU">
                        <Button
                          size="small"
                          variant="outlined"
                          color="success"
                          startIcon={<AddCircleIcon />}
                          onClick={() => openCreateSkuDialog(sku)}
                        >
                          Create
                        </Button>
                      </Tooltip>
                    </Stack>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      ) : unmappedSkus.length > 0 ? (
        <Card variant="outlined">
          <CardContent>
            <Typography color="text.secondary" align="center">
              No SKUs match your search criteria
            </Typography>
          </CardContent>
        </Card>
      ) : null}

      {/* SKU Mapping Dialog */}
      <Dialog
        open={mappingDialogOpen}
        onClose={() => setMappingDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <LinkIcon color="primary" />
            Map SKU to Inventory
          </Box>
        </DialogTitle>
        <DialogContent>
          {/* DT-1922: this dialog stays open when its action fails; the page Alert sits behind the modal. */}
          {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
          {selectedUnmappedSku && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
              {/* Unmapped SKU Info */}
              <Alert severity="info">
                <AlertTitle>Order SKU: {selectedUnmappedSku.sku}</AlertTitle>
                This SKU has {formatNumber(selectedUnmappedSku.total_quantity)} units across {formatNumber(selectedUnmappedSku.transaction_count)} transactions that are not being tracked in inventory.
                {selectedUnmappedSku.gtin && (
                  <Typography variant="caption" component="div" sx={{ mt: 0.5 }}>
                    GTIN: {selectedUnmappedSku.gtin}
                  </Typography>
                )}
                {selectedUnmappedSku.asin && (
                  <Typography variant="caption" component="div">
                    ASIN: {selectedUnmappedSku.asin}
                  </Typography>
                )}
              </Alert>

              {/* Inventory SKU Selection */}
              <Typography variant="subtitle2" gutterBottom>
                Select the inventory SKU this order SKU should map to:
              </Typography>
              <Autocomplete
                options={inventorySkuOptions}
                getOptionLabel={(option) => `${option.primary_sku} - ${option.product_name}`}
                value={selectedInventorySku}
                onChange={(event, newValue) => setSelectedInventorySku(newValue)}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Inventory SKU"
                    placeholder="Search inventory SKUs..."
                    fullWidth
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {inventorySkuLoading ? <CircularProgress color="inherit" size={20} /> : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
                renderOption={(props, option) => (
                  <li {...props} key={option.id || option.primary_sku}>
                    <Box>
                      <Typography variant="body1" fontWeight="medium">
                        {option.primary_sku}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {option.product_name}
                      </Typography>
                    </Box>
                  </li>
                )}
                isOptionEqualToValue={(option, value) => option.id === value?.id}
                noOptionsText={inventorySkuLoading ? "Loading..." : "No matching inventory SKUs found"}
                loading={inventorySkuLoading}
                disabled={inventorySkuLoading}
              />

              {selectedInventorySku && (
                <Alert severity="success">
                  <AlertTitle>Mapping Preview</AlertTitle>
                  <Typography variant="body2">
                    <strong>{selectedUnmappedSku.sku}</strong> (order SKU) will be linked to <strong>{selectedInventorySku.primary_sku}</strong> (inventory SKU)
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    After mapping, future stock calculations will properly track this SKU.
                  </Typography>
                </Alert>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setMappingDialogOpen(false)} disabled={mappingLoading}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="primary"
            startIcon={<LinkIcon />}
            onClick={handleSubmitMapping}
            disabled={!selectedInventorySku || mappingLoading}
          >
            {mappingLoading ? <CircularProgress size={20} /> : 'Create Mapping'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Create New Inventory SKU Dialog */}
      <Dialog
        open={createSkuDialogOpen}
        onClose={() => setCreateSkuDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <AddCircleIcon color="success" />
            Create New Inventory SKU
          </Box>
        </DialogTitle>
        <DialogContent>
          {/* DT-1922: this dialog stays open when its action fails; the page Alert sits behind the modal. */}
          {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
          {selectedUnmappedSku && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
              {/* Info */}
              <Alert severity="info">
                <AlertTitle>Creating new inventory SKU from order SKU</AlertTitle>
                This will create a new entry in the product master. The new SKU will immediately start tracking inventory.
              </Alert>

              {/* SKU Field */}
              <TextField
                label="SKU *"
                value={newSkuForm.sku}
                onChange={(e) => setNewSkuForm({ ...newSkuForm, sku: e.target.value })}
                fullWidth
                helperText="This is the SKU that will be used for inventory tracking"
              />

              {/* Product Name Field */}
              <TextField
                label="Product Name"
                value={newSkuForm.product_name}
                onChange={(e) => setNewSkuForm({ ...newSkuForm, product_name: e.target.value })}
                fullWidth
                placeholder={newSkuForm.sku || 'Enter product name'}
                helperText="Optional - defaults to SKU if not provided"
              />

              {selectedBrand && (
                <Typography variant="body2" color="text.secondary">
                  This SKU will be created for brand: <strong>{selectedBrand.name}</strong>
                </Typography>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateSkuDialogOpen(false)} disabled={createSkuLoading}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="success"
            startIcon={<AddCircleIcon />}
            onClick={handleCreateNewSku}
            disabled={!newSkuForm.sku || createSkuLoading}
          >
            {createSkuLoading ? <CircularProgress size={20} /> : 'Create SKU'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default UnmappedSkuMappings;
